// One piece of feedback on the Feedback page: what was submitted (never editable),
// and the office's side — status, resolutions, the entrant it's about, and notes.
//
// Fetches the record itself from its id, as PersonnelDialog does, so the list never
// holds a copy that could be stale; `onChanged(record)` hands each save back.
//
// **Linking an entrant** shows their entry and their incidents on the event with the
// clinical content in, to a reader who may hold no incident permission (see backend
// routers/feedback.py). The link runs one way: nothing on the incident side says this
// feedback exists, so nothing here may add a way to open the incidents themselves —
// hence PatientIncidentHistory's `openable={false}`.
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel,
  FormGroup, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { API_BASE_URL } from "../config";
import {
  FEEDBACK_NATURE_COLOR, FEEDBACK_RESOLUTIONS, FEEDBACK_STATUSES,
} from "../constants";
import {
  consentText, daysUntil, feedbackEventText, feedbackLocationText, participantMatches,
  responseState, subjectName,
} from "../feedbackRules";
import { useMe } from "../MeContext";
import { notify } from "../notify";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";
import PatientIncidentHistory from "./PatientIncidentHistory";
import ParticipantDetailsDialog, {
  ViewParticipantButton, WatchListAlert, participantSummary,
} from "./ParticipantDetails";

const API = `${API_BASE_URL}/feedback`;
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const ACK_TEXT = {
  sent: "Acknowledgement emailed",
  outbox: "Acknowledgement written to the outbox (email isn't configured here)",
  failed: "Acknowledgement email failed",
};
const participantLabel = (p) =>
  [p.name || "(no name)", p.race_number, p.race].filter(Boolean).join(" — ");

const Line = ({ label, value }) => (!value ? null : (
  <Typography variant="body2">
    <Box component="span" sx={{ color: "text.secondary" }}>{label}: </Box>{value}
  </Typography>
));

const Section = ({ title, children, action }) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      <Typography variant="h6" sx={{ flex: 1 }}>{title}</Typography>
      {action}
    </Box>
    <Stack spacing={1}>{children}</Stack>
  </Box>
);

// When the formal response promised to a Complaint or Speaking Up is due — the same
// promise the acknowledgement email makes. Reads the saved status, not the unsaved
// Status picker, so it says where the record actually stands.
function ResponseDueBanner({ fb }) {
  const r = responseState(fb);
  if (!r) return null;
  const what = [
    (fb.nature || []).includes("Complaint") && "complaint",
    (fb.nature || []).includes("Speaking Up") && "speaking-up concern",
  ].filter(Boolean).join(" and ");
  const date = r.due.toLocaleDateString(undefined,
    { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  const days = daysUntil(r.due);
  const plural = (n) => `${n} day${n === 1 ? "" : "s"}`;
  if (r.state === "closed") {
    return <Alert severity="success">A formal response to this {what} was due by {date}. It's Closed.</Alert>;
  }
  if (r.state === "overdue") {
    return (
      <Alert severity="error">
        A formal response to this {what} was due by <strong>{date}</strong> — {plural(-days || 1)} overdue.
      </Alert>
    );
  }
  return (
    <Alert severity="warning">
      A formal response to this {what} is due by <strong>{date}</strong>
      {days > 0 ? ` — ${plural(days)} left.` : " — today."}
    </Alert>
  );
}

// Linking: a single whole-name match is offered for one click; otherwise the
// near matches, and a search over the whole entry list.
function ParticipantLinker({ fb, canEdit, onLink, busy }) {
  const [options, setOptions] = useState(null);
  const [search, setSearch] = useState(subjectName(fb));
  useEffect(() => {
    axios.get(`${API}/${fb.id}/participants`).then(res => setOptions(res.data)).catch(() => setOptions([]));
  }, [fb.id]);

  const suggested = useMemo(() => {
    if (!options) return { exact: [], partial: [] };
    return participantMatches(subjectName(fb), options);
  }, [fb, options]);

  if (options == null) return <CircularProgress size={20} />;
  if (options.length === 0) {
    return <Typography variant="body2" color="text.secondary">This event has no entry list to link to.</Typography>;
  }
  if (!canEdit) {
    return <Typography variant="body2" color="text.secondary">Not linked to an entrant.</Typography>;
  }
  const near = suggested.exact.length ? suggested.exact : suggested.partial.slice(0, 8);
  const who = subjectName(fb);
  return (
    <Stack spacing={1.5}>
      {suggested.exact.length === 1 ? (
        <Alert severity="info" action={
          <Button size="small" variant="contained" disabled={busy}
                  onClick={() => onLink(suggested.exact[0].id)}>Yes, link</Button>}>
          Is this <strong>{participantLabel(suggested.exact[0])}</strong> on the entry list?
        </Alert>
      ) : near.length > 0 ? (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {suggested.exact.length ? "Entrants" : "Near matches"} for "{who}":
          </Typography>
          <Stack spacing={0.5}>
            {near.map(p => (
              <Box key={p.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>{participantLabel(p)}</Typography>
                <Button size="small" disabled={busy} onClick={() => onLink(p.id)}>Link</Button>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : who ? (
        <Typography variant="body2" color="text.secondary">
          Nobody on the entry list matches "{who}".
        </Typography>
      ) : null}
      <Autocomplete
        options={options}
        value={null}
        inputValue={search}
        onInputChange={(_, v, reason) => reason !== "reset" && setSearch(v)}
        onChange={(_, p) => p && onLink(p.id)}
        getOptionLabel={participantLabel}
        filterOptions={(opts, { inputValue }) => {
          const m = participantMatches(inputValue, opts);
          return inputValue.trim() ? [...m.exact, ...m.partial] : opts;
        }}
        disabled={busy}
        renderInput={(params) => (
          <TextField {...params} size="small" label="Search the entry list"
                     helperText="By name or race number" />
        )}
      />
    </Stack>
  );
}

export default function FeedbackDialog({ feedbackId, onClose, onChanged, onDeleted }) {
  const fullScreen = useFullScreenDialog();
  const { can } = useMe();
  const canEdit = can("Feedback.edit");
  const [fb, setFb] = useState(null);
  const [work, setWork] = useState({ status: "", resolutions: [], resolution_other: "" });
  const [linked, setLinked] = useState(null);
  const [viewEntrant, setViewEntrant] = useState(null);
  const [note, setNote] = useState("");
  const { busy, run } = useWriteLock();

  const take = useCallback((data) => {
    setFb(data);
    setWork({ status: data.status, resolutions: data.resolutions || [],
              resolution_other: data.resolution_other || "" });
  }, []);

  useEffect(() => {
    if (feedbackId == null) return;
    setFb(null); setLinked(null); setNote("");
    axios.get(`${API}/${feedbackId}`).then(res => take(res.data)).catch(() => onClose());
    // Not on onClose: callers pass an inline function, and a new one each render
    // must not refetch the record.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackId, take]);

  const participantId = fb?.participant_id;
  useEffect(() => {
    setLinked(null);
    if (participantId == null || fb == null) return;
    axios.get(`${API}/${fb.id}/participant`).then(res => setLinked(res.data)).catch(() => {});
    // Only a change of link (or record) refetches, not every save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fb?.id, participantId]);

  if (feedbackId == null) return null;

  const put = (body, message) => run(() => axios.put(`${API}/${fb.id}`, body).then(res => {
    take(res.data);
    onChanged?.(res.data);
    if (message) notify(message, "success");
  }));

  const dirty = fb && (work.status !== fb.status
    || JSON.stringify(work.resolutions) !== JSON.stringify(fb.resolutions || [])
    || (work.resolution_other || "") !== (fb.resolution_other || ""));
  const otherMissing = work.resolutions.includes("Other") && !work.resolution_other.trim();

  const toggleResolution = (r) => setWork(w => ({
    ...w,
    resolutions: w.resolutions.includes(r) ? w.resolutions.filter(x => x !== r)
      : FEEDBACK_RESOLUTIONS.filter(x => x === r || w.resolutions.includes(x)),
  }));

  const addNote = () => run(() => axios.post(`${API}/${fb.id}/notes`, { details: note }).then(res => {
    const next = { ...fb, notes: [...fb.notes, res.data] };
    setFb(next); onChanged?.(next); setNote("");
  }));
  const deleteNote = (n) => {
    if (!window.confirm("Delete this note?")) return;
    run(() => axios.delete(`${API}/notes/${n.id}`).then(() => {
      const next = { ...fb, notes: fb.notes.filter(x => x.id !== n.id) };
      setFb(next); onChanged?.(next);
    }));
  };
  const deleteFeedback = () => {
    if (!window.confirm(`Delete ${fb.reference} permanently? Use this for spam and tests only — `
      + "a real piece of feedback should be closed instead.")) return;
    run(() => axios.delete(`${API}/${fb.id}`).then(() => { onDeleted?.(fb.id); onClose(); }));
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>
        {fb ? `${fb.reference} — ${(fb.nature || []).join(", ") || "Feedback"}` : "Feedback"}
      </DialogTitle>
      <DialogContent dividers>
        {!fb ? <CircularProgress /> : (
          <Stack spacing={3}>
            <Section title="Submitted">
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {(fb.nature || []).map(n => (
                  <Chip key={n} size="small" label={n} color={FEEDBACK_NATURE_COLOR[n] || "default"} />
                ))}
                <Chip size="small" variant="outlined"
                      label={fb.source === "staff" ? "From staff" : "Public form"} />
              </Box>
              {/* Each caption sits under the line it's about, in one Box so the
                  Stack's spacing doesn't pull them apart. */}
              <Box>
                <Line label="Submitted" value={fmtDateTime(fb.submitted_at)} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  IP address {fb.ip || "unknown"}{fb.user_agent ? ` · ${fb.user_agent}` : ""}
                </Typography>
              </Box>
              <Line label="From" value={fb.submitter_name || "(no name given)"} />
              <Line label="Phone" value={fb.submitter_phone} />
              <Box>
                <Line label="Email" value={fb.submitter_email} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {fb.submitter_email ? (ACK_TEXT[fb.acknowledgement] || "No acknowledgement recorded")
                    : "No email address, so no acknowledgement was sent"}
                </Typography>
              </Box>
              <Line label="On behalf of" value={fb.on_behalf_of} />
              <Line label="Consent" value={consentText(fb)} />
              <Line label="Event" value={feedbackEventText(fb)} />
              <Line label={fb.source === "staff" ? "Shift" : "Location"} value={feedbackLocationText(fb)} />
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>{fb.details}</Typography>
              </Box>
            </Section>

            <Divider />
            <Section title="Entrant"
                     action={canEdit && fb.participant_id != null && (
                       <Button size="small" disabled={busy}
                               onClick={() => put({ participant_id: null }, "Unlinked.")}>Unlink</Button>
                     )}>
              {fb.event_id == null ? (
                <Typography variant="body2" color="text.secondary">
                  Not about a listed event, so there's no entry list to link to.
                </Typography>
              ) : fb.participant_id != null ? (
                <>
                  {!linked ? <CircularProgress size={20} /> : (
                    <>
                      {/* As an incident's read-only patient line: the summary, and the
                          eye for everything else (components/ParticipantDetails.jsx). */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {participantSummary(linked.participant)}
                        </Typography>
                        <ViewParticipantButton onClick={() => setViewEntrant(linked.participant)} />
                      </Box>
                      <WatchListAlert participant={linked.participant} showScreening />
                      {linked.incidents.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No incidents recorded for them at this event.
                        </Typography>
                      ) : (
                        <PatientIncidentHistory incidents={linked.incidents} openable={false}
                          title={`${linked.incidents.length} incident${linked.incidents.length === 1 ? "" : "s"} for them at this event`} />
                      )}
                    </>
                  )}
                </>
              ) : (
                <ParticipantLinker fb={fb} canEdit={canEdit} busy={busy}
                                   onLink={(id) => put({ participant_id: id }, "Linked.")} />
              )}
            </Section>

            <Divider />
            <Section title="Follow-up">
              <ResponseDueBanner fb={fb} />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "center" } }}>
                <FormControl size="small" sx={{ minWidth: 200 }} disabled={!canEdit}>
                  <InputLabel id="fb-status">Status</InputLabel>
                  <Select labelId="fb-status" label="Status" value={work.status}
                          onChange={(e) => setWork(w => ({ ...w, status: e.target.value }))}>
                    {FEEDBACK_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Created → Following Up once it's been read and follow-up has started → Closed when
                  that's finished.
                </Typography>
              </Stack>
              <Typography variant="subtitle2">Resolutions</Typography>
              <FormGroup row>
                {FEEDBACK_RESOLUTIONS.map(r => (
                  <FormControlLabel key={r} label={r} disabled={!canEdit}
                                    control={<Checkbox checked={work.resolutions.includes(r)}
                                                       onChange={() => toggleResolution(r)} />} />
                ))}
              </FormGroup>
              {work.resolutions.includes("Other") && (
                <TextField label="Other resolution" fullWidth size="small" disabled={!canEdit}
                           value={work.resolution_other} error={otherMissing}
                           onChange={(e) => setWork(w => ({ ...w, resolution_other: e.target.value }))} />
              )}
              {canEdit && (
                <Box>
                  <Button variant="contained" disabled={!dirty || otherMissing || busy}
                          onClick={() => put(work, "Saved.")}>Save follow-up</Button>
                </Box>
              )}
            </Section>

            <Divider />
            <Section title={`Notes (${fb.notes.length})`}>
              {fb.notes.map(n => (
                <Box key={n.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1,
                                      display: "flex", gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{n.details}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {n.added_by || "—"} — {fmtDateTime(n.added_at)}
                    </Typography>
                  </Box>
                  {canEdit && (
                    <Tooltip title="Delete note">
                      <IconButton size="small" onClick={() => deleteNote(n)} disabled={busy}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
              {canEdit && (
                <Stack spacing={1}>
                  <TextField label="Add a note" fullWidth multiline minRows={2} value={note}
                             onChange={(e) => setNote(e.target.value)}
                             helperText="How the follow-up is going, the thinking behind a resolution…" />
                  <Box>
                    <Button variant="outlined" disabled={!note.trim() || busy} onClick={addNote}>
                      Add note
                    </Button>
                  </Box>
                </Stack>
              )}
            </Section>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {fb && can("Feedback.delete") && (
          <Button color="error" onClick={deleteFeedback} disabled={busy} sx={{ mr: "auto" }}>
            Delete
          </Button>
        )}
        {dirty && <Typography variant="caption" color="warning.main">Follow-up not saved</Typography>}
        <Button onClick={onClose} disabled={busy}>Close</Button>
      </DialogActions>
      {/* The server serves this reader the entrant's screening in full (Feedback.view
          — see backend routers/feedback.py), so it is shown as a screener sees it. */}
      <ParticipantDetailsDialog participant={viewEntrant} showScreening
                                onClose={() => setViewEntrant(null)} />
    </Dialog>
  );
}
